'use client'

import { AnimatePresence, motion } from 'framer-motion'
import { InfoIcon } from 'lucide-react'
import { useTranslations } from 'next-intl'

import { LayersIcon, TrendIcon } from '@/components/shell-icons'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import {
  HOME_LIST,
  HOME_ROW,
  HOME_SPRING,
  HomePanel,
  LiveDot,
} from '@/features/home/components/home-motion'
import type { HomeSnapshot } from '@/features/home/hooks/use-home-snapshot'
import { NEAR_LIQUIDATION, type PositionRisk, type RiskLevel } from '@/features/home/lib/home-risk'
import { useNumberFormat } from '@/hooks/use-number-format'
import { getExchangeLabel } from '@/lib/configs/exchange-registry'
import { cn } from '@/lib/utils'

const VISIBLE_ROWS = 5
// The distance bar's full width: anything 3× the "near" threshold away reads as a full bar.
const DISTANCE_SCALE = NEAR_LIQUIDATION * 3

const LEVEL_TEXT: Record<RiskLevel, string> = {
  danger: 'text-chart-red',
  warning: 'text-ox-gold',
  ok: 'text-chart-mint',
}
const LEVEL_BAR: Record<RiskLevel, string> = {
  danger: 'bg-chart-red',
  warning: 'bg-ox-gold',
  ok: 'bg-chart-mint',
}

function RiskRow({ risk }: { risk: PositionRisk }) {
  const t = useTranslations('trade.home.risk')
  const fmt = useNumberFormat()
  const { position } = risk
  const reason = risk.reasons[0]
  const distanceShare =
    risk.liquidationDistance === null
      ? null
      : Math.min(1, risk.liquidationDistance / DISTANCE_SCALE)

  return (
    <motion.li variants={HOME_ROW} className="border-foreground/5 border-b py-2.5 last:border-b-0">
      <div className="flex items-center gap-2">
        <span
          className={cn(
            'rounded-xs px-1.5 py-px text-[10px] font-medium uppercase',
            risk.side === 'long'
              ? 'bg-chart-mint/10 text-chart-mint'
              : 'bg-chart-red/10 text-chart-red'
          )}
        >
          {t(risk.side)}
        </span>
        <span className="text-foreground min-w-0 truncate text-sm tracking-tight">
          {position.symbol}
        </span>
        <span className="text-foreground/40 hidden text-xs lg:inline">
          {getExchangeLabel(position.exchange)}
        </span>
        {risk.leverage !== null && (
          <span
            className={cn(
              'bg-foreground/5 rounded-xs px-1.5 py-px text-[11px]',
              risk.reasons.includes('highLeverage') ? 'text-ox-gold' : 'text-foreground/60'
            )}
          >
            {fmt.number(risk.leverage, { maximumFractionDigits: 1 })}x
          </span>
        )}
        <span
          className={cn(
            'ml-auto text-sm whitespace-nowrap',
            (risk.unrealizedPnlUsd ?? 0) >= 0 ? 'text-chart-mint' : 'text-chart-red'
          )}
        >
          {risk.unrealizedPnlUsd === null
            ? '—'
            : fmt.usd(risk.unrealizedPnlUsd, { sensitive: true })}
        </span>
      </div>
      <div className="mt-2 flex items-center gap-3">
        <div className="bg-foreground/10 h-1 flex-1 overflow-hidden rounded-full">
          {distanceShare !== null && (
            <motion.div
              className={cn('h-full origin-left rounded-full', LEVEL_BAR[risk.level])}
              initial={{ scaleX: 0 }}
              animate={{ scaleX: Math.max(0.02, distanceShare) }}
              transition={HOME_SPRING}
            />
          )}
        </div>
        <span className={cn('w-36 shrink-0 text-right text-xs', LEVEL_TEXT[risk.level])}>
          {risk.liquidationDistance === null
            ? t('noLiquidation')
            : t('liquidationAway', {
                percent: fmt.number(risk.liquidationDistance * 100, { maximumFractionDigits: 1 }),
              })}
        </span>
      </div>
      {reason && (
        <p className={cn('mt-1.5 text-xs', LEVEL_TEXT[risk.level])}>{t(`reasons.${reason}`)}</p>
      )}
    </motion.li>
  )
}

interface HomeRiskCheckProps {
  snapshot: HomeSnapshot
  onOpenRiskSlides: () => void
  onOpenHedge: () => void
  className?: string
}

/**
 * A risk read of every open position for someone new to leverage: how far each one is from
 * liquidation (a bar that shrinks as the mark closes in), its leverage, and in plain words why
 * it is flagged. The fixes are one click away: stress-test the portfolio or hedge it.
 */
export function HomeRiskCheck({
  snapshot,
  onOpenRiskSlides,
  onOpenHedge,
  className,
}: HomeRiskCheckProps) {
  const t = useTranslations('trade.home.risk')
  const { risk } = snapshot
  const rows = risk.items.slice(0, VISIBLE_ROWS)

  return (
    <HomePanel
      title={
        <span className="flex items-center gap-1.5">
          {t('title')}
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                aria-label={t('whatIsLiquidation')}
                className="text-foreground/35 hover:text-foreground cursor-help transition-colors"
              >
                <InfoIcon className="size-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent className="max-w-72">{t('liquidationExplainer')}</TooltipContent>
          </Tooltip>
        </span>
      }
      className={className}
      aside={
        rows.length > 0 && (
          <AnimatePresence mode="wait" initial={false}>
            <motion.span
              key={risk.level}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={HOME_SPRING}
              className={cn(
                'flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap',
                risk.level === 'danger' && 'bg-chart-red/10 text-chart-red',
                risk.level === 'warning' && 'bg-ox-gold/10 text-ox-gold',
                risk.level === 'ok' && 'bg-chart-mint/10 text-chart-mint'
              )}
            >
              {risk.level !== 'ok' && <LiveDot />}
              {risk.level === 'ok' ? t('allClear') : t('toReview', { count: risk.flaggedCount })}
            </motion.span>
          </AnimatePresence>
        )
      }
    >
      {rows.length === 0 ? (
        <div className="flex flex-1 flex-col items-start justify-center gap-1 py-6">
          <p className="text-foreground text-sm">{t('emptyTitle')}</p>
          <p className="text-foreground/50 max-w-sm text-xs">{t('emptyHint')}</p>
        </div>
      ) : (
        <motion.ul variants={HOME_LIST} initial="hidden" animate="show" className="flex flex-col">
          {rows.map((item) => (
            <RiskRow key={item.key} risk={item} />
          ))}
        </motion.ul>
      )}
      {risk.items.length > VISIBLE_ROWS && (
        <p className="text-foreground/40 pt-2 text-xs">
          {t('more', { count: risk.items.length - VISIBLE_ROWS })}
        </p>
      )}
      <div className="mt-auto flex flex-wrap gap-2 pt-3">
        <Button variant="secondary" size="sm" onClick={onOpenRiskSlides} className="flex-1">
          <LayersIcon />
          {t('stressTest')}
        </Button>
        <Button variant="secondary" size="sm" onClick={onOpenHedge} className="flex-1">
          <TrendIcon />
          {t('hedge')}
        </Button>
      </div>
    </HomePanel>
  )
}
